import { Notification, NotificationStatusEnums, NotificationTypeEnums } from 'config/types/notification'
import { useRouter } from 'hooks/useRouter'
import { useCallback, useState } from 'react'
import NotificationService from 'services/NotificationService'
import { useUnreadNotificationAmount } from 'state/notification/hooks'
import styled from 'styled-components'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image'
import Link from 'UIKit/Link/Link'
import Skeleton from 'UIKit/Skeleton'
import Text from 'UIKit/Text'
import TransText from 'UIKit/Text/TransText'
import { TimeSinceDetails } from 'UIKit/TimeSince/TimeSinceDetails'
import { RowCenter, RowMiddle } from '../Row'

export interface NotificationItemProps extends BoxProps {
  notification: Notification
  hideExtendDescButton?: boolean
}

export const NotificationItemLoader = () => {
  return (
    <StyledContainer height={96} width={['100%', '100%', '100%']} $isUnread={false}>
      <Skeleton width={90} height={70} />
      <RowCenter flexDirection="column" width="100%" ml={2}>
        <Skeleton width="100%" height={40} />
        <Skeleton width="100%" height={16} mt={2} />
      </RowCenter>
    </StyledContainer>
  )
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, ...props }) => {
  const { unreadPrivateAmount, unreadSystemAmount, updateUnreadPrivateAmount, updateUnreadSystemAmount } =
    useUnreadNotificationAmount()

  const [isUnread, setIsUnread] = useState<boolean>(notification.status === NotificationStatusEnums.UNREAD)
  const router = useRouter()
  const descriptionArray = notification.description?.split('\n')

  const handleDirectNotificationUrl = useCallback(() => {
    if (notification.external) {
      window.open(notification.url, '__blank')
    } else router.push(notification.url)
  }, [notification])

  const handleUpdateUnreadAmount = useCallback(async () => {
    if (!isUnread) return
    let response
    if (notification.type === NotificationTypeEnums.PRIVATE)
      response = await NotificationService.markAsReadPrivateNotification([notification.id])
    else response = await NotificationService.markAsReadSystemNotification([notification.id])

    if (response.code === 'success') {
      setIsUnread(false)

      if (notification.type === NotificationTypeEnums.PRIVATE) updateUnreadPrivateAmount(unreadPrivateAmount - 1)
      else if (notification.type === NotificationTypeEnums.SYSTEM) updateUnreadSystemAmount(unreadSystemAmount - 1)
    }
  }, [unreadPrivateAmount, unreadSystemAmount, isUnread, notification])

  return (
    <StyledContainer
      as={notification.external ? Link : 'a'}
      external={notification.external}
      href={notification.url}
      width="100%"
      $isUnread={isUnread}
      onClick={(e) => {
        if (!e.ctrlKey) {
          e.preventDefault()
          handleDirectNotificationUrl()
          handleUpdateUnreadAmount()
        }
      }}
      {...props}
    >
      <Box minWidth={80} minHeight={80}>
        <StyledNotificationThumbnail
          width={80}
          height={80}
          src={notification.bannerUrl || '/images/notification/default_thumbnail.png'}
          alt="Notification Thumbnail"
        />
      </Box>

      <Flex flexDirection="column" ml="12px" height="100%" width="100%" pb={0}>
        <Text fontSize="14px" fontWeight="700" textAlign={'left'} color="text_primary">
          {notification.title}
        </Text>

        <StyledDescriptionContainer flexDirection="column">
          <StyledToggleExpandContainer>
            {descriptionArray?.length
              ? descriptionArray.map((message) => {
                  return <StyledDescription as="p">{message}</StyledDescription>
                })
              : null}
          </StyledToggleExpandContainer>
        </StyledDescriptionContainer>

        <RowMiddle mt="4px">
          {isUnread && <StyledUnreadNotiDot mr="4px" />}

          <StyledTimeSince date={new Date(notification.createTime)} />
        </RowMiddle>

        {notification.anchorText && (
          <StyledButton
            width="100%"
            height="32px"
            as={notification.external ? Link : 'a'}
            external={notification.external}
            href={notification.url}
            p="8px 20px"
            borderRadius="40px"
            mt="8px"
          >
            <TransText fontSize="12px" fontWeight={700} textAlign="center" color="text_primary">
              {notification.anchorText}
            </TransText>
          </StyledButton>
        )}
      </Flex>
    </StyledContainer>
  )
}

const StyledTimeSince = styled(TimeSinceDetails)`
  color: ${({ theme: { colors } }) => colors.text_disabled};
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  text-align: left;
`
const StyledButton = styled(RowCenter)`
  background: ${({ theme: { colors } }) => colors.card_primary_active};

  &:hover {
    background: ${({ theme: { colors } }) => colors.card_primary_active_alt};
  }
`
const StyledContainer = styled(Flex)<{ $isUnread: boolean }>`
  margin: 0px 0px 16px 0px;
  padding: 12px;
  align-items: flex-start !important;
  cursor: pointer;
  border-radius: 12px;
  max-width: 100%;
  background: ${({ theme }) => theme.colors.card_primary};

  &:hover {
    background: ${({ theme }) => theme.colors.card_primary_active};
    ${StyledButton} {
      background: ${({ theme }) => theme.colors.card_primary_active_alt};
    }
    ${StyledTimeSince} {
      color: ${({ theme: { colors } }) => colors.text_tertiary};
    }
  }
`

const StyledUnreadNotiDot = styled(Box)`
  background: ${({ theme: { colors } }) => colors.text_link_secondary};
  width: 8px;
  height: 8px;
  border-radius: 50%;
`

const StyledNotificationThumbnail = styled(Image)`
  img {
    border-radius: ${({ theme: { radii } }) => radii.tiny};
  }
`

const StyledDescriptionContainer = styled(Flex)`
  margin-top: 8px;
  position: relative;
`
const StyledDescription = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  word-break: break-word;

  color: ${({ theme: { colors } }) => colors.text_secondary} !important;
`

const StyledToggleExpandContainer = styled(Box)`
  will-change: transition;
  transition: ${({ theme: { transitions } }) => transitions.medium};
`

export default NotificationItem
